import {AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {valueExists} from './forms';
import moment from 'moment';
import {PHONE_PATTERN} from '../_constants/constants';
import {PhoneNumberUtil} from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * A validator to check if the field matches another.  The two fields must be a part of the same form group.
 * You'll probably want to trigger validation on the field when the first field is updated.  For example, if
 * it's two email fields, the second has this validator, then the first should trigger the second to revalidate
 * if it's changed. <br />
 * You can do that with this snippet in the input tag:
 * <pre>
 *   (ngModelChange)="c.confirmEmail.updateValueAndValidity()"
 * </pre>
 *
 * @param controlNameToMatch The name of the field to match against
 */
export function fieldsMatch(controlNameToMatch: string): ValidatorFn {
  return (control: AbstractControl) => {
    const otherFieldValue: string = control.parent ? control.parent.controls[controlNameToMatch].value : null;
    return otherFieldValue === control.value ? null : {notSame: true};
  };
}

export function valueIsNot(valueToNotMatch: string): ValidatorFn {
  return (control: AbstractControl) => control.value !== valueToNotMatch && {required: true} || null;
}

export function enterOneOf(controlNames: string[]): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const controls = controlNames?.map(c => group?.get(c) ?? (group as UntypedFormGroup)?.controls?.[c]);
    return controls?.find(valueExists) ? null : {oneOf: true};
  };
}

/**
 * A MOD 10 validator to verify a SIN is valid.
 */
export function sinValidator(control: AbstractControl): ValidationErrors | null {
  const len = control.value?.length;
  let errors;
  if(len > 0 && len !== 9) {
    errors = {sin: true};
  } else {
    let value: number;
    let sum = 0;
    for(let index = 0 ; index < len ; index++) {
      value = Number(control.value.charAt(index));
      if(index % 2) {
        value *= 2;
        if(value > 9) {
          value = 1 + (value % 10);
        }
      }
      sum += value;
    }
    errors = sum % 10 === 0 ? null : {sin: true};
  }
  return errors;
}

/**
 * A MOD 10 validator to verify a SIN is valid. But also accepts last 4 digit SIN without checking the validity
 */
export function fourOrNineSinValidator(control: AbstractControl): ValidationErrors | null {
  const len = control.value?.length;
  let errors;
  if(len > 0 && len !== 4 && len !== 9) {
    errors = {sin: true};
  } else if(len === 9) {
    let value: number;
    let sum = 0;
    for(let index = 0 ; index < len ; index++) {
      value = Number(control.value.charAt(index));
      if(index % 2) {
        value *= 2;
        if(value > 9) {
          value = 1 + (value % 10);
        }
      }
      sum += value;
    }
    errors = sum % 10 === 0 ? null : {sin: true};
  }
  return errors;
}

export function dateValidator(control: AbstractControl): ValidationErrors | null {
  //Don't validate null/empty strings.  Those fields will optionally have Required validators to catch if that's a problem.
  if(!control?.value || control.value.toString() === '') {
    return null;
  }
  return moment(control.value, 'YYYYMMDD').isValid() ? null : {invalidDate: true};
}

export function phoneValidator(control: AbstractControl): ValidationErrors | null {
  //Don't validate null/empty strings.  Those fields will optionally have Required validators to catch if that's a problem.
  if(!control?.value || control.value.toString() === '') {
    return null;
  }

  return (Validators.pattern(PHONE_PATTERN)(control) && {phone: true}) || (phoneUtil.isValidNumber(phoneUtil.parse(control.value, 'CA')) ? null : {phone: true});
}
